import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  AppBar,
  Toolbar,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Assignment,
  Close,
  AttachFile,
  Download,
  Warning,
  CheckCircle,
  Business,
  Person,
  Email,
  Phone,
  LocationOn,
  Inventory,
  Category,
  Today,
  Description,
  ExpandMore,
  Comment,
} from '@mui/icons-material';
import { useAuth } from 'react-oidc-context';
import appConfig from '../config/app';
import EnhancedCommentSystem from './EnhancedCommentSystem';

// API service functions (reusing the same createApiService from RMAReviewSystem)
const createApiService = (auth) => ({
  async getRMADetails(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/details/?detailed=true`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  },

  async getRMAComments(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/comments/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  },

  async addRMAComment(rmaId, comment, commentType = 'general', visibleToCustomer = true) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/comments/add/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        comment,
        comment_type: commentType,
        visible_to_customer: visibleToCustomer,
      }),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  },

  async getRMAFieldIssues(rmaId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/${rmaId}/field-issues/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  },

  async respondToFieldIssue(issueId, response) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const apiResponse = await fetch(`${appConfig.api.baseUrl}/rma/field-issues/${issueId}/respond/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
      body: JSON.stringify({
        customer_response: response,
      }),
    });
    if (!apiResponse.ok) throw new Error(`HTTP error! status: ${apiResponse.status}`);
    return apiResponse.json();
  },

  async downloadAttachment(attachmentId) {
    const token = auth.user?.access_token;
    const cognito_user_id = auth.user?.profile?.sub;
    const response = await fetch(`${appConfig.api.baseUrl}/rma/attachments/${attachmentId}/download/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Cognito-User-ID': cognito_user_id,
      },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.blob();
  },
});

const fieldLabels = {
  company_name: 'Company Name',
  contact_person: 'Contact Person',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  city: 'City',
  postal_code: 'Postal Code',
  country: 'Country',
  product_name: 'Product Name',
  product_code: 'Product Code',
  serial_number: 'Serial Number',
  purchase_date: 'Purchase Date',
  delivery_note_number: 'Delivery Note Number',
  description: 'Description',
};

const fieldIcons = {
  company_name: <Business />,
  contact_person: <Person />,
  email: <Email />,
  phone: <Phone />,
  address: <LocationOn />,
  city: <LocationOn />,
  postal_code: <LocationOn />,
  country: <LocationOn />,
  product_name: <Inventory />,
  product_code: <Category />,
  serial_number: <CheckCircle />,
  purchase_date: <Today />,
  delivery_note_number: <Assignment />,
  description: <Description />,
};

// Simplified Field Display Component (No editing)
const FieldDisplay = ({ fieldName, label, value, hasIssue }) => (
  <Box
    sx={{
      p: 2,
      border: hasIssue ? '2px solid' : '1px solid',
      borderColor: hasIssue ? 'error.main' : 'grey.300',
      borderRadius: 1,
      bgcolor: hasIssue ? 'error.50' : 'background.paper',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      {fieldIcons[fieldName]}
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {hasIssue && <Chip size="small" label="Issue" color="error" variant="outlined" />}
    </Box>
    <Typography variant="body1" sx={{ minHeight: '24px' }}>
      {value || '-'}
    </Typography>
  </Box>
);

// Simplified Field Issue Card Component
const FieldIssueCard = ({ issue, onRespond }) => {
  const [responseText, setResponseText] = useState('');
  const [showResponse, setShowResponse] = useState(false);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'error';
      default: return 'default';
    }
  };

  const handleSubmitResponse = () => {
    if (responseText.trim()) {
      onRespond(issue.id, responseText.trim());
      setResponseText('');
      setShowResponse(false);
    }
  };

  return (
    <Accordion key={issue.id} sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {fieldIcons[issue.field_name]}
            <Typography variant="subtitle1">{fieldLabels[issue.field_name]}</Typography>
          </Box>
          <Chip size="small" label={issue.severity.toUpperCase()} color={getSeverityColor(issue.severity)} />
          {issue.resolved && <Chip size="small" label="Resolved" color="success" icon={<CheckCircle />} />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: 'block' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Issue Description:
          </Typography>
          <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
            {issue.description || 'No description available'}
          </Typography>
        </Box>

        {issue.customer_response && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Your Response:
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {issue.customer_response}
            </Typography>
          </Box>
        )}

        {!issue.resolved && !issue.customer_response && (
          <Button
            variant="outlined"
            startIcon={<Comment />}
            onClick={() => setShowResponse(!showResponse)}
          >
            Respond
          </Button>
        )}

        {showResponse && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Enter your response..."
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmitResponse}
                disabled={!responseText.trim()}
              >
                Submit Response
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowResponse(false)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const CustomerRMAReviewSystem = ({ rmaId, onBack }) => {
  const auth = useAuth();
  const [selectedRMA, setSelectedRMA] = useState(null);
  const [fieldIssues, setFieldIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', type: 'info' });
  const [tabValue, setTabValue] = useState(0);

  const apiService = createApiService(auth);

  const showNotification = (message, type = 'info') => {
    setNotification({ open: true, message, type });
    setTimeout(() => setNotification({ ...notification, open: false }), 5000);
  };

  const getStatusColor = (status) => {
    switch (status) {
          case 'submitted': return 'primary';
          case 'under_review': return 'warning';
          case 'approved': return 'success';
          case 'rejected': return 'error';
          case 'needs_info': return 'info';
          case 'return_to_producer': return 'warning';
          case 'return_to_customer': return 'success';
          case 'in_progress': return 'info';
          case 'completed': return 'success';
          case 'cancelled': return 'error';
          default: return 'default';
        }
  };

  const handleRespondToIssue = async (issueId, response) => {
    try {
      await apiService.respondToFieldIssue(issueId, response);
      setFieldIssues(fieldIssues.map(issue => (issue.id === issueId ? { ...issue, customer_response: response } : issue)));
      showNotification('Response submitted successfully', 'success');
    } catch (error) {
      showNotification('Error submitting response', 'error');
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const blob = await apiService.downloadAttachment(attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showNotification('Attachment downloaded successfully', 'success');
    } catch (error) {
      showNotification('Error downloading attachment', 'error');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [rmaData, fieldIssuesData] = await Promise.all([
        apiService.getRMADetails(rmaId),
        apiService.getRMAFieldIssues(rmaId),
      ]);

      setSelectedRMA(rmaData);
      setFieldIssues(
        Array.isArray(fieldIssuesData?.field_issues)
          ? fieldIssuesData.field_issues.map(issue => ({
              ...issue,
              description: issue.issue_description || issue.description || 'No description available',
            }))
          : []
      );
    } catch (error) {
      showNotification(`Error loading data: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (rmaId) {
      loadData();
    }
  }, [rmaId]);

  const unresolved = fieldIssues.filter(issue => !issue.resolved);
  const resolved = fieldIssues.filter(issue => issue.resolved);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedRMA) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">RMA data not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar position="static" elevation={1} sx={{ bgcolor: '#126F42' }}>
        <Toolbar>
          {onBack && (
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
              <Close />
            </IconButton>
          )}
          <Assignment sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            RMA Details - {selectedRMA.rma_number}
          </Typography>
          <Chip
            label="Customer View"
            color="primary"
            sx={{ ml: 2 }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {notification.open && (
          <Alert severity={notification.type} sx={{ mb: 2 }} onClose={() => setNotification({ ...notification, open: false })}>
            {notification.message}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h4" gutterBottom>
                  {selectedRMA.rma_number}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {selectedRMA.company_name} - {selectedRMA.contact_person}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {selectedRMA.product_name} ({selectedRMA.product_code})
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <Chip label={selectedRMA.status.replace('_', ' ').toUpperCase()} color={getStatusColor(selectedRMA.status)} size="large" />
                  <Badge badgeContent={unresolved.length} color="error">
                    <Warning />
                  </Badge>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

          <Paper sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="RMA Details" />
              <Tab label={`Field Issues (${unresolved.length})`} />
              <Tab label="Comments" />
            </Tabs>
          </Paper>

          {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6">RMA Information</Typography>
                  <Grid container spacing={2}>
                    {Object.entries(fieldLabels).map(([fieldName, label]) => {
                      const value = selectedRMA[fieldName] || '';
                      const hasIssue = fieldIssues.some(issue => issue.field_name === fieldName && !issue.resolved);
                      return (
                        <Grid item xs={12} md={6} key={fieldName}>
                          <FieldDisplay
                            fieldName={fieldName}
                            label={label}
                            value={value}
                            hasIssue={hasIssue}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Attachments ({selectedRMA.attachments?.length || 0})
                  </Typography>
                  <List>
                    {selectedRMA.attachments?.map((attachment) => (
                      <ListItem key={attachment.id}>
                        <ListItemIcon>
                          <AttachFile />
                        </ListItemIcon>
                        <ListItemText
                          primary={attachment.original_filename || attachment.filename}
                          secondary={`${Math.round((attachment.file_size || attachment.size) / 1024)} KB`}
                        />
                        <IconButton color="primary" onClick={() => handleDownloadAttachment(attachment.id, attachment.original_filename || attachment.filename)}>
                          <Download />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>

              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Last Updated
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(selectedRMA.updated_at).toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

          {tabValue === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">Field Issues ({fieldIssues.length})</Typography>
                    {unresolved.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" color="error" gutterBottom>
                          Unresolved Issues ({unresolved.length})
                        </Typography>
                        {unresolved.map((issue) => (
                          <FieldIssueCard
                            key={issue.id}
                            issue={issue}
                            onRespond={handleRespondToIssue}
                          />
                        ))}
                      </Box>
                    )}

                    {resolved.length > 0 && (
                      <Box>
                        <Typography variant="h6" color="success.main" gutterBottom>
                          Resolved Issues ({resolved.length})
                        </Typography>
                        {resolved.map((issue) => (
                          <FieldIssueCard
                            key={issue.id}
                            issue={issue}
                            onRespond={handleRespondToIssue}
                          />
                        ))}
                      </Box>
                    )}

                    {fieldIssues.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary">
                          No field issues found
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {tabValue === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <EnhancedCommentSystem
                  rmaId={rmaId}
                  isServiceman={false}
                  onNotification={showNotification}
                />
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    );
};

export default CustomerRMAReviewSystem;